import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';

interface ShareTemplateHrZonesProps {
  workout: ShareableWorkout;
  transparent?: boolean;
}

const zoneColors: Record<string, string> = {
  z1: '#60a5fa',
  z2: '#34d399',
  z3: '#fbbf24',
  z4: '#fb923c',
  z5: '#f87171',
};

export const ShareTemplateHrZones = forwardRef<HTMLDivElement, ShareTemplateHrZonesProps>(
  ({ workout, transparent = false }, ref) => {
    const { t } = useTranslation();
    const hr = workout.heart_rate;
    const zones = hr?.zones ?? [];
    const buckets = hr?.buckets ?? {};
    const totalSec = Object.values(buckets).reduce((a, b) => a + (b ?? 0), 0);

    const bg = transparent
      ? 'transparent'
      : 'linear-gradient(160deg, #0a1628 0%, #0f1f35 45%, #071018 100%)';

    return (
      <div
        ref={ref}
        style={{
          width: 1080,
          height: 1920,
          background: bg,
          color: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          display: 'flex',
          flexDirection: 'column',
          padding: 72,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
          <img src={straivLogo} alt="Straiv" style={{ height: 56, objectFit: 'contain' }} />
        </div>

        <h1
          style={{
            fontSize: 52,
            fontWeight: 800,
            textAlign: 'center',
            margin: '0 0 12px',
            letterSpacing: '-0.02em',
          }}
        >
          {workout.title || t('components.shareWorkoutExtra.templates.hrZonesFallbackTitle', { defaultValue: 'Run' })}
        </h1>
        <p style={{ textAlign: 'center', opacity: 0.65, fontSize: 28, margin: '0 0 48px' }}>
          {new Date(workout.completed_at).toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 24,
            marginBottom: 56,
          }}
        >
          {[
            { label: 'Distance', value: workout.distance_km != null ? `${workout.distance_km.toFixed(2)} km` : '—' },
            { label: 'Avg HR', value: hr?.avgBpm != null ? `${hr.avgBpm} bpm` : '—' },
            { label: 'Peak HR', value: hr?.peakBpm != null ? `${hr.peakBpm} bpm` : '—' },
          ].map((stat) => (
            <div
              key={stat.label}
              style={{
                background: transparent ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.06)',
                borderRadius: 20,
                padding: '28px 20px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 22, opacity: 0.6, marginBottom: 8 }}>{stat.label}</div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{stat.value}</div>
            </div>
          ))}
        </div>

        <h2 style={{ fontSize: 34, fontWeight: 700, margin: '0 0 28px' }}>Heart rate zones</h2>

        {totalSec <= 0 ? (
          <p style={{ fontSize: 28, opacity: 0.7 }}>No heart rate data for this run.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>
            {zones.map((z) => {
              const sec = buckets[z.id] ?? 0;
              const pct = totalSec > 0 ? Math.round((sec / totalSec) * 100) : 0;
              const mins = Math.floor(sec / 60);
              const secs = sec % 60;
              return (
                <div key={z.id}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 26,
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {z.label} ({z.lowerBpm}–{z.upperBpm})
                    </span>
                    <span style={{ opacity: 0.75 }}>
                      {mins}:{String(secs).padStart(2, '0')} · {pct}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 28,
                      borderRadius: 14,
                      background: transparent ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(pct, sec > 0 ? 4 : 0)}%`,
                        height: '100%',
                        borderRadius: 14,
                        background: zoneColors[z.id] ?? '#0affc2',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p
          style={{
            textAlign: 'center',
            fontSize: 24,
            opacity: 0.45,
            marginTop: 'auto',
            paddingTop: 32,
          }}
        >
          straiv.app
        </p>
      </div>
    );
  },
);

ShareTemplateHrZones.displayName = 'ShareTemplateHrZones';
